<script setup lang="ts">
import { Pause, Play } from 'lucide-vue-next';
import type { HomeOptionwayAirlinesProperties } from '@optionway/models';

const properties = defineProps<HomeOptionwayAirlinesProperties>();

const descriptionId = useId();
const prefix = 'components.home.optionwayAirlines';
const isPaused = ref(false);

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.homeOptionwayAirlines;
const componentConfig = computed<HomeOptionwayAirlinesProperties>(() => ({
  sellableLogos: properties.sellableLogos ?? config?.sellableLogos ?? 'all',
  numberOfAirlines: properties.numberOfAirlines ?? config?.numberOfAirlines ?? 350,
  title: properties.title ?? config?.title,
  description: properties.description ?? config?.description,
}));

const row1Logos = [
  'V7',
  'U2',
  'TO',
  'AF',
  'VY',
  'KL',
  'IB',
  'XK',
  'LH',
  'W6',
  'AT',
  'SN',
  'SS',
  'EI',
  'TK',
  'OS',
  'TP',
  'BA',
  'LX',
  'LA',
  'TS',
  'TU',
  'AH',
  'ET',
  'DY',
  'A3',
  'VN',
];
const row2Logos = [
  '3O',
  'MU',
  'EK',
  'AA',
  'QR',
  'TG',
  'CZ',
  'AZ',
  'GQ',
  'BJ',
  'SK',
  'SQ',
  'UU',
  'MK',
  'G3',
  'UA',
  'T7',
  '5O',
  'HY',
  'Y4',
  'TB',
  'CX',
  'FM',
  'MS',
  'LM',
  'EW',
  'EY',
  'AY',
];
const expandedRow1 = expandForInfiniteCarousel(row1Logos);
const expandedRow2 = expandForInfiniteCarousel(row2Logos);
</script>

<template>
  <section
    aria-labelledby="airlines-title"
    class="grid w-full grid-cols-1 justify-items-center gap-10"
  >
    <div class="flex flex-col items-center gap-6 px-4 text-center md:px-6">
      <h2
        id="airlines-title"
        class="text-2xl font-semibold leading-tight text-text-primary-900 md:text-3xl"
      >
        <span class="text-brand-primary-700">
          {{ $t(`${prefix}.titleNumber`, { number: componentConfig.numberOfAirlines }) }}
        </span>
        {{ useLocalized(componentConfig.title) }}
      </h2>
      <p class="text-sm font-normal text-text-secondary-600">
        {{ $t(`${prefix}.tagline`) }}
      </p>
    </div>
    <span
      :id="descriptionId"
      class="sr-only"
    >
      {{ useLocalized(componentConfig.description) }}
    </span>

    <div
      v-if="componentConfig.sellableLogos === 'all'"
      class="group flex w-full flex-col gap-3"
      :aria-describedby="descriptionId"
    >
      <div class="relative w-full overflow-hidden">
        <div
          class="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-white to-transparent md:w-20"
        />
        <div
          class="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-white to-transparent md:w-20"
        />

        <div class="flex flex-col gap-6">
          <div :class="['flex w-max motion-reduce:animate-none', isPaused ? 'animate-none' : 'animate-slider']">
            <div
              v-for="iteration in 2"
              :key="`row1-${iteration}`"
              class="flex gap-3 pr-3"
            >
              <div
                v-for="(code, index) in expandedRow1"
                :key="`row1-${iteration}-${index}`"
                class="flex h-11 w-24 shrink-0 items-center justify-center rounded-lg bg-background p-2 md:h-14 md:w-28"
              >
                <UiCustomAirlineLogo
                  :iata-code="code"
                  class="max-h-5 w-auto max-w-full md:max-h-7"
                />
              </div>
            </div>
          </div>

          <div :class="['flex w-max motion-reduce:animate-none', isPaused ? 'animate-none' : 'animate-slider-reverse']">
            <div
              v-for="iteration in 2"
              :key="`row2-${iteration}`"
              class="flex gap-3 pr-3"
            >
              <div
                v-for="(code, index) in expandedRow2"
                :key="`row2-${iteration}-${index}`"
                class="flex h-11 w-24 shrink-0 items-center justify-center rounded-lg bg-background p-2 md:h-14 md:w-28"
              >
                <UiCustomAirlineLogo
                  :iata-code="code"
                  class="max-h-5 w-auto max-w-full md:max-h-7"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end px-4 md:px-6">
        <button
          type="button"
          class="inline-flex size-8 items-center justify-center rounded-full border border-gray-200 bg-white opacity-0 shadow-sm transition-all hover:bg-gray-50 focus:opacity-100 group-hover:opacity-100"
          :aria-label="isPaused ? $t(`${prefix}.play`) : $t(`${prefix}.pause`)"
          @click="isPaused = !isPaused"
        >
          <Pause
            v-if="!isPaused"
            class="size-3.5 text-text-primary-900"
            :stroke-width="2"
            aria-hidden="true"
          />
          <Play
            v-else
            class="size-3.5 text-text-primary-900"
            :stroke-width="2"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>

    <div
      v-else
      class="flex justify-center"
    >
      <UiCustomResponsivePicture
        src="home/sellable-airlines/all.png"
        sizes="100vw"
        :alt="$t(`${prefix}.allAlt`)"
        class="block sm:hidden"
        :aria-describedby="descriptionId"
      />

      <UiCustomResponsivePicture
        src="home/sellable-airlines/all-small.png"
        sizes="100vw"
        :alt="$t(`${prefix}.allAlt`)"
        class="hidden sm:block"
        :aria-describedby="descriptionId"
      />
    </div>
  </section>
</template>
